import React from 'react';
import { IconLogout } from '../ui/icons';
import { cx } from '../ui/cx';

function initials(name) {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}

// Header variant styling. "customer" is a flat navy (brand primary) bar.
// "driver" shares the navy bar but adds a flat amber accent underline so the
// two personas read as clearly different without fragmenting the brand palette.
// Flat fills only — no gradients. All text colors are fully opaque.
const VARIANT_STYLES = {
  customer: {
    wrapper: 'bg-primary border-b border-primary-dark',
    accentLine: null,
    logoBg: 'bg-white/20',
    brand: 'text-white',
    sub: 'text-white',
    avatarName: 'text-white',
    avatarSub: 'text-white',
    logoutBtn:
      'text-white hover:bg-white/15 focus:ring-white/70',
  },
  driver: {
    wrapper: 'bg-primary border-b border-primary-dark',
    accentLine: 'bg-accent',
    logoBg: 'bg-white/20',
    brand: 'text-white',
    sub: 'text-white',
    avatarName: 'text-white',
    avatarSub: 'text-white',
    logoutBtn:
      'text-white hover:bg-white/15 focus:ring-white/70',
  },
  light: {
    wrapper: 'bg-white border-b border-border',
    accentLine: null,
    logoBg: 'bg-primary',
    brand: 'text-primary',
    sub: 'text-body/60',
    avatarName: 'text-primary',
    avatarSub: 'text-body/60',
    logoutBtn:
      'text-body/40 hover:text-body hover:bg-surface focus:ring-primary/40',
  },
  // Peoria Charter: near-black bar with a Peoria-red accent underline. The logo
  // chip stays white so the red bird mark reads. Peoria-only — R3 never uses it.
  peoria: {
    wrapper: 'bg-peoria-bg border-b border-peoria-border',
    accentLine: 'bg-peoria-red',
    logoBg: 'bg-white',
    brand: 'text-peoria-text',
    sub: 'text-peoria-muted',
    avatarName: 'text-peoria-text',
    avatarSub: 'text-peoria-muted',
    logoutBtn:
      'text-peoria-muted hover:text-peoria-text hover:bg-peoria-surface2 focus:ring-peoria-red/40',
  },
};

function AppHeader({ userName, onLogout, subtitle, variant = 'customer', logoSrc, brandName }) {
  const roleLabel = subtitle || 'BOOK A RIDE';
  const s = VARIANT_STYLES[variant] || VARIANT_STYLES.customer;
  return (
    <header className={cx('shrink-0 relative z-10', s.wrapper)}>
      <div className="h-16 px-5 flex items-center gap-4">
        {logoSrc ? (
          // Branded lockup: logo in a white chip (so a colored mark reads on the navy
          // bar) + brand name, with the role label as an eyebrow beneath it.
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-btn bg-white shadow-card flex items-center justify-center shrink-0 overflow-hidden">
              <img src={logoSrc} alt={brandName || 'logo'} className="w-7 h-7 object-contain" />
            </div>
            <div className="leading-tight min-w-0">
              {brandName && (
                <div className={cx('text-15 font-semibold truncate max-w-[180px]', s.brand)}>
                  {brandName}
                </div>
              )}
              <div className={cx('text-11 uppercase tracking-brand truncate', s.sub)}>
                {roleLabel}
              </div>
            </div>
          </div>
        ) : (
          <div className="leading-none min-w-0">
            <div className={cx('text-11 uppercase tracking-brand truncate font-semibold', s.sub)}>
              {roleLabel}
            </div>
          </div>
        )}

        <div className="ml-auto flex items-center gap-3 min-w-0">
          {userName && (
            <div className="hidden sm:flex items-center gap-2.5 pl-2 pr-2 py-1 rounded-lg">
              <div className={cx('w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center shrink-0',
                variant === 'peoria' ? 'bg-peoria-red text-white' : 'bg-white/20 text-white')}>
                {initials(userName) || 'U'}
              </div>
              <div className="text-left leading-tight min-w-0">
                <div className={cx('text-13 font-semibold truncate max-w-[160px]', s.avatarName)}>
                  {userName}
                </div>
                <div className={cx('text-11 uppercase tracking-brand', s.avatarSub)}>
                  {roleLabel}
                </div>
              </div>
            </div>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              title="Sign out"
              aria-label="Sign out"
              className={cx(
                'w-9 h-9 rounded-btn cursor-pointer flex items-center justify-center transition-all duration-150',
                'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent',
                s.logoutBtn,
              )}
            >
              <IconLogout size={18} />
            </button>
          )}
        </div>
      </div>
      {s.accentLine && (
        <div className={cx('absolute left-0 right-0 bottom-0 h-[2px] pointer-events-none', s.accentLine)} />
      )}
    </header>
  );
}

export default AppHeader;
